/**
 * MarketplacePayoutsPage — seller-facing ledger of paid digital-download
 * orders with amounts and net-of-fees. Sellers see exactly what landed and
 * what will hit their Stripe balance after processing fees.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Store, DollarSign, TrendingUp, Receipt, RefreshCw, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function auth() {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

function fmt(amount, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);
  } catch { return `${currency} ${(amount || 0).toFixed(2)}`; }
}

export default function MarketplacePayoutsPage() {
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('paid');

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API}/api/marketplace/payouts/orders?status_filter=${filter}&limit=200`, auth()),
        axios.get(`${API}/api/marketplace/payouts/summary`, auth()),
      ]);
      setOrders(oRes.data?.orders || []);
      setTotals(oRes.data?.totals || null);
      setSummary(sRes.data || null);
    } catch (e) {
      // silent
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6" data-testid="marketplace-payouts-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-fuchsia-500 flex items-center justify-center">
            <Store className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Marketplace Payouts</h1>
            <p className="text-sm text-slate-500">Every paid digital-download order, with amounts and net after processing fees.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard
          icon={DollarSign}
          label="All-time gross"
          amount={fmt(summary?.all_time?.gross || 0)}
          hint={`${summary?.all_time?.count || 0} paid order${summary?.all_time?.count === 1 ? '' : 's'}`}
          colour="emerald"
          testid="summary-alltime-gross"
        />
        <SummaryCard
          icon={TrendingUp}
          label="All-time net (after fees)"
          amount={fmt(summary?.all_time?.net || 0)}
          hint="What lands on your Stripe balance"
          colour="fuchsia"
          testid="summary-alltime-net"
        />
        <SummaryCard
          icon={Receipt}
          label="Last 30 days · net"
          amount={fmt(summary?.last_30d?.net || 0)}
          hint={`${summary?.last_30d?.count || 0} paid order${summary?.last_30d?.count === 1 ? '' : 's'}`}
          colour="sky"
          testid="summary-30d-net"
        />
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['paid', 'pending', 'all'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            data-testid={`payouts-filter-${f}`}
          >
            {f[0].toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Ledger table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading orders…</div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No {filter} orders yet</p>
              <p className="text-xs text-slate-400 mt-1">
                List a digital download on the Marketplace and orders will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Product</th>
                    <th className="text-left px-4 py-3">Buyer</th>
                    <th className="text-right px-4 py-3">Gross</th>
                    <th className="text-right px-4 py-3">Fees</th>
                    <th className="text-right px-4 py-3">Net</th>
                    <th className="text-center px-4 py-3">Status</th>
                    <th className="text-center px-4 py-3">Downloads</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map(o => (
                    <tr key={o.id} className="hover:bg-slate-50" data-testid={`payout-row-${o.id}`}>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {(o.paid_at || o.created_at || '').slice(0, 10) || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-800 max-w-xs truncate">{o.product_name || o.product_id.slice(-6)}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[220px] truncate">
                        {o.buyer_email || <span className="text-slate-400">anonymous</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-800 tabular-nums">{fmt(o.amount, o.currency)}</td>
                      <td className="px-4 py-3 text-right text-slate-500 tabular-nums">−{fmt(o.processing_fee, o.currency)}</td>
                      <td className="px-4 py-3 text-right text-emerald-700 font-semibold tabular-nums">{fmt(o.net_amount, o.currency)}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge
                          className={
                            o.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' :
                            o.payment_status === 'pending' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' :
                            'bg-slate-100 text-slate-600 hover:bg-slate-100'
                          }
                        >
                          {o.payment_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-slate-500">
                        {o.download_count} / {o.max_downloads}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {totals && filter === 'paid' && (
                  <tfoot className="bg-slate-50 text-xs">
                    <tr>
                      <td className="px-4 py-3 text-slate-500" colSpan={3}>Totals ({totals.paid_count} paid)</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-700 tabular-nums">{fmt(totals.gross)}</td>
                      <td className="px-4 py-3 text-right text-slate-600 tabular-nums">−{fmt(totals.processing_fees)}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700 tabular-nums">{fmt(totals.net)}</td>
                      <td className="px-4 py-3" colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {totals?.fee_note && (
        <div className="text-xs text-slate-500 italic">{totals.fee_note} <a href="https://stripe.com/pricing" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline inline-flex items-center gap-1">Learn more <ExternalLink className="h-3 w-3" /></a></div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, amount, hint, colour = 'emerald', testid }) {
  const colours = {
    emerald: 'from-emerald-500 to-emerald-600',
    fuchsia: 'from-fuchsia-500 to-fuchsia-600',
    sky: 'from-sky-500 to-sky-600',
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colours[colour]} flex items-center justify-center text-white`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-xl font-bold text-slate-900 tabular-nums">{amount}</div>
          <div className="text-[11px] text-slate-500 truncate">{hint}</div>
        </div>
      </CardContent>
    </Card>
  );
}
